import { Component, HostListener } from '@angular/core'
import {
    RouteConfigLoadStart,
    RouteConfigLoadEnd,
    Router
} from '@angular/router'

import { LOCAL_STORAG_KEYS } from '@@constants/local-storage-keys'
import cryptoDecryptData from '@@utils/cryptoDecryptData'
import cryptoEncryptData from '@@utils/cryptoEncryptData'
import getBoolean from '@@utils/getBoolean'
import isRtlLang from '@@utils/isRtlLang'

import { LoaderInterceptorService } from '@@services/loader.service'
import { GeneralService } from '@@services/general.service'
import { AuthService } from '@@services/auth.service'
import { BoxService } from '@@services/box.service'
import { AppService } from '@@services/app.service'
import { VersionCheckService } from '@@services/version-check.service'
import { LanguageService } from '@@services/language.service'
import { UserTourService } from '@@services/user-tour.service'

import { environment } from '../environments/environment'
import { CUSTOM_EVENTS } from '@@constants/custom-events'
import { TranslateService } from '@ngx-translate/core'
import {
    getTranslationLoadFailurePrompt,
    shouldPromptForMissingTranslations
} from '@@shared/utils/translation-load-failure'
import { HubSpotService } from '@@services/hubspot.service'
import { JwtHelperService } from '@auth0/angular-jwt'
import moment from 'moment'

@Component({
    standalone: false,
    selector: 'app-root',
    templateUrl: './app.component.html'
})
export class AppComponent {
    public toastyComponentPosition: any
    static refreshPopupOpened: boolean = false
    static limitedAccessPopup: boolean = false
    static translationLoadFailedPopup: boolean = false
    public loadingRouteConfig: boolean = false
    public primaryColor = GeneralService.in2PrimaryColor
    private appContentCleared = false

    constructor(
        private router: Router,
        private boxService: BoxService,
        private authService: AuthService,
        private appService: AppService,
        private translationService: TranslateService,
        private hubSpotService: HubSpotService,
        private translate: TranslateService,
        private versionCheckService: VersionCheckService,
        private languageService: LanguageService,
        private userTourService: UserTourService
    ) {
        // G3: remove dead ipAddress override (no writer in codebase; stale value routes all API calls to wrong host)
        localStorage.removeItem('ipAddress')

        // Set portal version
        localStorage.setItem(
            LOCAL_STORAG_KEYS.PORTAL_VERSION,
            environment.version.toString()
        )

        // Priority 1: Check if user profile exists with saved language preference
        const userProfile = localStorage.getItem(LOCAL_STORAG_KEYS.USER_PROFILE)

        if (userProfile) {
            try {
                const profile = JSON.parse(userProfile)
                const userLanguage = profile?.language?.locale || profile?.defaultLanguage

                if (userLanguage) {
                    this.translationService.setDefaultLang(userLanguage)
                    this.translationService.use(userLanguage)
                    localStorage.setItem(LOCAL_STORAG_KEYS.LANGUAGE_SELECTED, userLanguage)
                    // Set moment locale to match
                    this.languageService.setLanguage(userLanguage)
                    return
                }
            } catch (e) {
                console.error('App Component - Error parsing user profile:', e)
            }
        }

        // Priority 2: Check localStorage language
        const savedLang = localStorage.getItem(LOCAL_STORAG_KEYS.LANGUAGE_SELECTED)

        if (savedLang && savedLang !== 'en') {
            this.translationService.setDefaultLang(savedLang)
            this.translationService.use(savedLang)
            // Set moment locale to match
            this.languageService.setLanguage(savedLang)
            return
        }

        // Priority 3: Default to English
        this.translationService.setDefaultLang('en')
        this.translationService.use('en')
        // Set moment locale to English
        this.languageService.setLanguage('en')
    }

    ngOnInit() {
        this.translationService.onLangChange.subscribe(() => {
            this.applyDirectionClass()
        })

        // Debug: Check what language TranslateService is actually using after init
        setTimeout(() => {
        }, 1000)

        let enableMultiThermalPrintMachines =
            localStorage.getItem(
                LOCAL_STORAG_KEYS.ENABLE_MULTI_THERMAL_PRINT_MACHINES
            ) &&
            getBoolean(
                localStorage.getItem(
                    LOCAL_STORAG_KEYS.ENABLE_MULTI_THERMAL_PRINT_MACHINES
                )
            )

        // Initialize HubSpot tracking if user is logged in
        if (this.authService.isLoggedIn() && environment.hubspotEnabled) {
            // Initialize tracking for the first page load
            this.hubSpotService.initializeHubSpotTracking()

            // Set up automatic page tracking (moved from HubSpotService constructor to avoid circular dependencies)
            this.hubSpotService.setupPageTracking()
        }

        // Initialize UserTour with user identification if logged in
        // Skip UserTour when customer support is using admin password to avoid wasting sessions
        const jwtHelper = new JwtHelperService()
        const token = localStorage.getItem(LOCAL_STORAG_KEYS.IN2_USER_TOKEN)
        let isAdminPassword = false
        try {
            if (token) {
                const tokenInfo = jwtHelper.decodeToken(token)
                isAdminPassword = tokenInfo && tokenInfo.isAdminPassword && getBoolean(tokenInfo.isAdminPassword)
            }
        } catch (e) {}

        if (!isAdminPassword) {
            if (this.authService.isLoggedIn()) {
                const userProfile = localStorage.getItem(LOCAL_STORAG_KEYS.USER_PROFILE)
                if (userProfile) {
                    try {
                        const profile = JSON.parse(userProfile)
                        const in2PackageType = cryptoDecryptData(localStorage.getItem(LOCAL_STORAG_KEYS.IN2_PACKAGE_TYPE))
                        const creationDate = cryptoDecryptData(localStorage.getItem(LOCAL_STORAG_KEYS.USER_CREATION_DATE))
                        this.userTourService.init(
                            profile.id?.toString() || profile.email,
                            {
                                name: profile.name,
                                email: profile.email,
                                signed_up_at: creationDate ? new Date(+creationDate).toISOString() : undefined,
                                ...(in2PackageType ? { in2_package_type: in2PackageType } : {})
                            }
                        )
                    } catch (e) {
                        this.userTourService.init()
                    }
                } else {
                    this.userTourService.init()
                }
            } else {
                this.userTourService.init()
            }
        }

        // Start version checking for production builds
        if (environment.production && environment.version > 0) {
            this.versionCheckService.startVersionChecking()
        }

        if (enableMultiThermalPrintMachines) {
            try {
                setTimeout(() => {
                    this.appService.getPcIps().subscribe((res) => {
                        if (res) {
                            var ips = res.ips.split(',')
                            localStorage.setItem(
                                LOCAL_STORAG_KEYS.PC_IPS,
                                JSON.stringify(ips)
                            )
                            localStorage.setItem(
                                LOCAL_STORAG_KEYS.HOST_NAME,
                                res.hostName
                            )
                        }
                    })
                }, 2000)
            } catch (e) {
                console.error('Error getting PC IPs:', e)
            }
        }

        this.router.events.subscribe((event) => {
            if (event instanceof RouteConfigLoadStart) {
                this.loadingRouteConfig = true
            } else if (event instanceof RouteConfigLoadEnd) {
                this.loadingRouteConfig = false
            }
        })

        LoaderInterceptorService.stackCount = 0

        const roleId = localStorage.getItem(LOCAL_STORAG_KEYS.ROLE_ID)
        if (roleId && roleId.length == 1)
            localStorage.setItem(
                LOCAL_STORAG_KEYS.ROLE_ID,
                cryptoEncryptData(+roleId)
            )
        // adding this storage event listener to handle changes on local storage setup
        window.addEventListener('storage', this.handleStorageChange.bind(this))
    }

    handleStorageChange(event: StorageEvent) {
        // If localStorage is cleared, or a key like 'In2-User-Token' is removed, redirect
        if (
            localStorage.length < 2 ||
            (event.key === 'In2-User-Token' && event.newValue === null)
        ) {
            this.router.navigate(['/portal/access/login'])
        }

        // Handle tenant context change in another tab
        if (
            event.key === 'companySelectionRouteActivated' &&
            !window.location.pathname.startsWith('/account')
        ) {
            this.clearAppComponentChildren()
            this.createFullScreenOverlay()
        }
    }

    private clearAppComponentChildren(): void {
        if (this.appContentCleared) return
        try {
            const appRoot = document.querySelector(
                'app-root'
            ) as HTMLElement | null
            if (appRoot) {
                while (appRoot.firstChild) {
                    appRoot.removeChild(appRoot.firstChild as ChildNode)
                }
            }
            this.appContentCleared = true
        } catch (err) {
            console.error('Failed to clear app-root children', err)
        }
    }


    private applyDirectionClass(): void {
        const isRTL = isRtlLang(this.translationService.currentLang)
        document.body.dir = isRTL ? 'rtl' : 'ltr'
        document.body.classList.toggle('rtl', isRTL)
        // Arabic only (not Urdu, Farsi, Hebrew): used to scope the Tajawal font
        document.body.classList.toggle('lang-ar', this.translationService.currentLang === 'ar')
    }

    /**
     * Creates a full-screen overlay that blocks user interaction
     */
    private createFullScreenOverlay() {
        // Get translated texts
        const title = this.translate.instant('tenantOverlay.title')
        const message1 = this.translate.instant('tenantOverlay.message1')
        const message2 = this.translate.instant('tenantOverlay.message2')
        const message3 = this.translate.instant('tenantOverlay.message3')
        const buttonText = this.translate.instant('tenantOverlay.button')

        // Create overlay div
        const overlayDiv = document.createElement('div')
        overlayDiv.id = 'tenant-context-overlay'
        overlayDiv.style.position = 'fixed'
        overlayDiv.style.top = '0'
        overlayDiv.style.left = '0'
        overlayDiv.style.width = '100%'
        overlayDiv.style.height = '100%'
        overlayDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'
        overlayDiv.style.zIndex = '99999'
        overlayDiv.style.display = 'flex'
        overlayDiv.style.justifyContent = 'center'
        overlayDiv.style.alignItems = 'center'

        overlayDiv.innerHTML = `
    <div style="background-color: white; padding: 30px; border-radius: 4px; text-align: center; max-width: 500px; box-shadow: 0 5px 15px rgba(0,0,0,.5); min-width: 320px;">
      <i class="material-icons" style="color: #f44336; font-size: 64px; display: block; margin: 0 auto 20px auto;">cancel</i>
      <h2>${title}</h2>
      <p>${message1}</p>
      <p>${message2}</p>
      <p>${message3}</p>
      <button class="mat-raised-button" style="background-color: ${GeneralService.in2PrimaryColor}; color: white; padding: 8px 16px; border: none; border-radius: 4px; cursor: pointer; margin-top: 20px;"
        onclick="(function(){
          try{
            var t=localStorage.getItem('idtenant');
            var has=!(t&&t!=='null'&&t!=='undefined'&&(!t.trim||t.trim().length>0));
            window.location.href=has?'/portal':'/portal/company/selection';
          }catch(e){
            window.location.href='/portal/company/selection';
          }
        })()">
        ${buttonText}
      </button>
    </div>
  `

        document.body.appendChild(overlayDiv)
    }

    /**
     * Force reload the application with cache bypass
     */
    private forceReload(): void {
        // Clear all caches if possible
        if ('caches' in window) {
            caches.keys().then((names) => {
                names.forEach((name) => {
                    caches.delete(name)
                })
            })
        }

        // Store a flag to indicate we're refreshing due to connection issue
        try {
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem('refreshingAfterConnectionLoss', 'true');
            }
        } catch (e) {
            console.warn('Failed to set sessionStorage flag:', e);
        }

        // Force reload bypassing cache by using cache-busting query parameter
        // This is more reliable than window.location.reload() which may still use cache
        const url = new URL(window.location.href)
        url.searchParams.set('_v', Date.now().toString())
        // Use replace instead of assign to prevent back button issues
        window.location.replace(url.toString())
    }

    private isConnectionIssueLoop(): boolean {
        const key = 'connIssueFireTimes'
        const now = Date.now()
        try {
            const stored = sessionStorage.getItem(key)
            const times: number[] = stored ? JSON.parse(stored) : []
            const recent = times.filter((t) => now - t < 60_000)
            recent.push(now)
            sessionStorage.setItem(key, JSON.stringify(recent))
            return recent.length >= 3
        } catch {
            return false
        }
    }

    private async runConnectivityDiagnostic(): Promise<{ titleKey: string; messageKey: string }> {
        const base = 'portal.app-component'
        if (!navigator.onLine) {
            return { titleKey: `${base}.offline-title`, messageKey: `${base}.offline-message` }
        }
        const probe = (url: string, opts: RequestInit): Promise<boolean> =>
            fetch(url, opts).then(() => true).catch(() => false)
        const withTimeout = (p: Promise<boolean>): Promise<boolean> =>
            Promise.race([p, new Promise<boolean>((r) => setTimeout(() => r(false), 3000))])
        const internetOk = await withTimeout(
            probe('https://www.gstatic.com/generate_204', { mode: 'no-cors', cache: 'no-store' })
        )
        if (!internetOk) {
            return { titleKey: `${base}.offline-title`, messageKey: `${base}.offline-message` }
        }
        const portalOk = await withTimeout(
            probe(`/version.json?_=${Date.now()}`, { cache: 'no-store' })
        )
        if (!portalOk) {
            return { titleKey: `${base}.portal-blocked-title`, messageKey: `${base}.portal-blocked-message` }
        }
        return { titleKey: `${base}.api-blocked-title`, messageKey: `${base}.api-blocked-message` }
    }

    @HostListener('document:version-refresh-alert', ['$event'])
    versionRefresh(event: any) {
        if (event.detail.data) {
            if (!AppComponent.refreshPopupOpened) {
                AppComponent.refreshPopupOpened = true

                // Only call signInWithToken if user is logged in
                if (this.authService.isLoggedIn()) {
                    this.authService.signInWithToken().subscribe((res) => {
                        if (res.token) {
                            localStorage.setItem(
                                LOCAL_STORAG_KEYS.IN2_USER_TOKEN,
                                res.token
                            )
                            GeneralService.customEvent.emit(
                                CUSTOM_EVENTS.TOKEN_CHANGED
                            )
                            localStorage.setItem(
                                LOCAL_STORAG_KEYS.USER_NAME,
                                res.profile.name
                            )
                            localStorage.setItem(
                                LOCAL_STORAG_KEYS.USER_IMAGE,
                                res.profile.picUrl
                            )
                            localStorage.setItem(
                                LOCAL_STORAG_KEYS.USER_ID,
                                res.profile.id
                            )
                            res.profile.token = res.token
                        localStorage.setItem(
                            LOCAL_STORAG_KEYS.USER_PROFILE,
                            JSON.stringify(res.profile)
                        )
                        AuthService.loggedInUser = res.profile
                    }
                })
                }

                let width = '400px'
                this.boxService
                    .boxPopup({
                        maxWidth: width,
                        width: width,
                        disableClose: true,
                        data: {
                            status: {
                                confirm: true,
                                cancel: false,
                                delete: false,
                                custom: false
                            },
                            label: {
                                title: 'portal.app-component.new-version',
                                confirm: 'portal.app-component.refresh',
                                cancel: '',
                                delete: '',
                                custom: ''
                            },
                            class: {
                                title: '',
                                confirm: 'maincolor-background',
                                cancel: '',
                                delete: '',
                                custom: '',
                                text: ''
                            },
                            content: {
                                text: 'portal.app-component.new-version-message'
                            }
                        }
                    })
                    .subscribe((result) => {
                        if (result) {
                            AppComponent.refreshPopupOpened = false

                            this.forceReload()
                        }

                        this.boxService.unsubscribeFnCustom()
                    })
            }

            setTimeout(() => {
                AppComponent.refreshPopupOpened = false
            }, 5000)
        }
    }

    @HostListener('document:limited-access', ['$event'])
    limitedAccess(event: any) {
        if (event.detail.data) {
            if (!AppComponent.limitedAccessPopup) {
                AppComponent.limitedAccessPopup = true
                const persistent = this.isConnectionIssueLoop()
                const diagnosticPromise = persistent
                    ? Promise.resolve({
                          titleKey: 'portal.app-component.persistent-issue-title',
                          messageKey: 'portal.app-component.persistent-issue-message'
                      })
                    : this.runConnectivityDiagnostic()
                diagnosticPromise.then(({ titleKey, messageKey }) => {
                    let width = '600px'
                    this.boxService
                        .boxPopup({
                            maxWidth: width,
                            width: width,
                            disableClose: true,
                            data: {
                                status: {
                                    confirm: true,
                                    cancel: true,
                                    delete: false,
                                    custom: false
                                },
                                label: {
                                    title: titleKey,
                                    confirm: 'portal.app-component.ok',
                                    cancel: 'portal.app-component.refresh',
                                    delete: '',
                                    custom: ''
                                },
                                class: {
                                    title: '',
                                    confirm: 'maincolor-background',
                                    cancel: 'maincolor-background',
                                    delete: '',
                                    custom: '',
                                    text: ''
                                },
                                text: messageKey,
                                content: {},
                                closeOnCancel: true,
                                closeOnConfirm: true
                            }
                        })
                        .subscribe((result) => {
                            AppComponent.limitedAccessPopup = false
                            if (!result) {
                                this.forceReload()
                            }
                        })
                })
            }
            setTimeout(() => {
                AppComponent.limitedAccessPopup = false
            }, 8000)
        }
    }

    // NB-5629: the page's language has no words (the file still failed after RetryingTranslateLoader's retries,
    // or CustomMissingTranslationHandler found it missing or empty). The wording is built in because the language
    // files are what failed. Refresh only: without its words the page is unusable (every button reads like
    // "btnOkText"), so there is no OK back to it. No timed flag reset: the flag clears when the popup closes.
    @HostListener('document:translation-load-failed', ['$event'])
    translationLoadFailed(event: any) {
        const failedLang = event?.detail?.data?.lang
        const shouldPrompt = shouldPromptForMissingTranslations(
            failedLang,
            this.translationService.currentLang,
            this.translationService.translations
        )
        if (!shouldPrompt || AppComponent.translationLoadFailedPopup) {
            return
        }
        AppComponent.translationLoadFailedPopup = true
        const prompt = getTranslationLoadFailurePrompt(
            failedLang,
            this.isConnectionIssueLoop()
        )
        const width = '600px'
        this.boxService
            .boxPopup({
                maxWidth: width,
                width: width,
                disableClose: true,
                data: {
                    status: {
                        confirm: false,
                        cancel: true,
                        delete: false,
                        custom: false
                    },
                    label: {
                        title: prompt.title,
                        confirm: '',
                        cancel: prompt.refresh,
                        delete: '',
                        custom: ''
                    },
                    class: {
                        title: '',
                        confirm: 'maincolor-background',
                        cancel: 'maincolor-background',
                        delete: '',
                        custom: '',
                        text: ''
                    },
                    text: prompt.message,
                    content: {},
                    closeOnCancel: true,
                    closeOnConfirm: true
                }
            })
            .subscribe(() => {
                AppComponent.translationLoadFailedPopup = false
                this.forceReload()
            })
    }
}
